import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { Checkbox as RCheckbox, Switch as RSwitch } from "radix-ui";
import { cx } from "../lib/cx";
import { Callout } from "./Notice";
import { IconAlertCircle, IconCheck, IconEye, IconEyeOff } from "./icons";

export function Label({ htmlFor, children, className }: { htmlFor?: string; children: ReactNode; className?: string }) {
  return (
    <label htmlFor={htmlFor} className={cx("block text-[13px] font-semibold text-fg", className)}>
      {children}
    </label>
  );
}

type FieldProps = {
  label: ReactNode;
  /** Short help shown under the label. */
  hint?: ReactNode;
  error?: string | null | undefined;
  optional?: boolean;
  className?: string;
  /** A single control. It receives id, aria-describedby and aria-invalid. */
  children: ReactElement<Record<string, unknown>>;
};

/** Label + control + hint + error, wired together for screen readers. */
export function Field({ label, hint, error, optional, className, children }: FieldProps) {
  const id = useId();
  const controlId = (children.props.id as string | undefined) ?? `f${id}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errId = error ? `${controlId}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cx("space-y-1.5", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={controlId}>{label}</Label>
        {optional ? <span className="text-[12px] text-dim">Optional</span> : null}
      </div>
      {isValidElement(children)
        ? cloneElement(children, {
            id: controlId,
            "aria-describedby": describedBy,
            "aria-invalid": error ? true : undefined,
          })
        : children}
      {hint ? (
        <p id={hintId} className="text-[12.5px] leading-relaxed text-dim">
          {hint}
        </p>
      ) : null}
      {error ? <FieldMessage id={errId}>{error}</FieldMessage> : null}
    </div>
  );
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { mono?: boolean; /** Select the whole value on focus, for prefilled fields. */ selectOnFocus?: boolean }
>(function Input({ className, mono, selectOnFocus, onFocus, onMouseUp, ...rest }, ref) {
  const justFocused = useRef(false);
  return (
    <input
      ref={ref}
      className={cx("field", mono && "font-mono text-[13px]", className)}
      onFocus={(e) => {
        if (selectOnFocus) {
          e.currentTarget.select();
          justFocused.current = true;
        }
        onFocus?.(e);
      }}
      onMouseUp={(e) => {
        // A click that focused the field would otherwise drop the selection on mouseup.
        if (justFocused.current) {
          e.preventDefault();
          justFocused.current = false;
        }
        onMouseUp?.(e);
      }}
      {...rest}
    />
  );
});

/** Password input with a show/hide toggle. */
export const PasswordInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function PasswordInput(
  { className, ...rest },
  ref,
) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input ref={ref} type={show ? "text" : "password"} className={cx("field pr-11", className)} {...rest} />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Hide password" : "Show password"}
        aria-pressed={show}
        className="absolute inset-y-1 right-1 inline-flex w-9 items-center justify-center rounded-md text-dim hover:bg-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-brand-500"
      >
        {show ? <IconEyeOff size={17} /> : <IconEye size={17} />}
      </button>
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, ...rest },
  ref,
) {
  return <textarea ref={ref} className={cx("field min-h-24 resize-y", className)} {...rest} />;
});

/** A native select (best for keyboard and mobile); styles.css gives every select our chevron. */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={cx("field", className)} {...rest}>
      {children}
    </select>
  );
});

export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  chip,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  /** What turning it on does ("Block AI crawlers"), or a name that can't be read two ways ("Page cache"). */
  label: string;
  description?: ReactNode;
  /** A state chip after the label, where the switch's position alone doesn't say it ("Blocked", "Unlocked"). */
  chip?: ReactNode;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        {chip ? (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <label htmlFor={id} className="text-[14px] font-medium text-fg">
              {label}
            </label>
            {chip}
          </div>
        ) : (
          <label htmlFor={id} className="text-[14px] font-medium text-fg">
            {label}
          </label>
        )}
        {description ? <p className="mt-0.5 text-[13px] text-dim">{description}</p> : null}
      </div>
      <RSwitch.Root
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="relative mt-0.5 inline-flex h-5.5 w-9.5 shrink-0 items-center rounded-full bg-line-strong transition-colors duration-150 data-[state=checked]:bg-brand-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:opacity-50"
      >
        <RSwitch.Thumb className="block h-4.5 w-4.5 translate-x-0.5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,.25)] transition-transform duration-150 ease-out data-[state=checked]:translate-x-[18px]" />
      </RSwitch.Root>
    </div>
  );
}

/** A switch without its own label, for a table of them; `label` is its accessible name. */
export function BareSwitch({ checked, onCheckedChange, label, disabled }: { checked: boolean; onCheckedChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <RSwitch.Root
      aria-label={label}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className="relative inline-flex h-5.5 w-9.5 shrink-0 items-center rounded-full bg-line-strong transition-colors duration-150 data-[state=checked]:bg-brand-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:opacity-50"
    >
      <RSwitch.Thumb className="block h-4.5 w-4.5 translate-x-0.5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,.25)] transition-transform duration-150 ease-out data-[state=checked]:translate-x-[18px]" />
    </RSwitch.Root>
  );
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <RCheckbox.Root
        id={id}
        checked={checked}
        onCheckedChange={(v) => onCheckedChange(v === true)}
        disabled={disabled}
        className="mt-0.5 inline-flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-[5px] bg-surface shadow-[inset_0_0_0_1.5px_var(--hx-line-strong)] transition-colors data-[state=checked]:bg-brand-600 data-[state=checked]:shadow-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
      >
        <RCheckbox.Indicator className="text-white">
          <IconCheck size={13} strokeWidth={2.5} />
        </RCheckbox.Indicator>
      </RCheckbox.Root>
      <div>
        <label htmlFor={id} className="text-[14px] text-fg">
          {label}
        </label>
        {description ? <p className="mt-0.5 text-[13px] text-dim">{description}</p> : null}
      </div>
    </div>
  );
}

/** A checkbox without a visible label of its own (in a table row): name it with `label`. */
export function BareCheckbox({
  checked,
  onCheckedChange,
  label,
  disabled,
  describedBy,
}: {
  checked: boolean | "indeterminate";
  onCheckedChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
  describedBy?: string;
}) {
  return (
    <RCheckbox.Root
      checked={checked}
      onCheckedChange={(v) => onCheckedChange(v === true)}
      disabled={disabled}
      aria-label={label}
      aria-describedby={describedBy}
      className="inline-flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-[5px] bg-surface shadow-[inset_0_0_0_1.5px_var(--hx-line-strong)] transition-colors data-[state=checked]:bg-brand-600 data-[state=checked]:shadow-none data-[state=indeterminate]:bg-brand-600 data-[state=indeterminate]:shadow-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:cursor-not-allowed disabled:bg-panel disabled:opacity-60"
    >
      <RCheckbox.Indicator className="text-white">
        {checked === "indeterminate" ? <IconMinusSmall /> : <IconCheck size={13} strokeWidth={2.5} />}
      </RCheckbox.Indicator>
    </RCheckbox.Root>
  );
}

function IconMinusSmall() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 12h12" stroke="currentColor" strokeWidth={3} strokeLinecap="round" />
    </svg>
  );
}

/** The one look for a problem with one field or one small control, under it: rose, an icon, short words. */
export function FieldMessage({ id, role, className, children }: { id?: string; role?: "alert"; className?: string; children: ReactNode }) {
  if (!children) return null;
  return (
    <p id={id} role={role} className={cx("flex items-start gap-1.5 text-[12.5px] font-medium text-bad", className)}>
      <IconAlertCircle size={14} className="mt-[2px] shrink-0" />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

/** A form-level error, for messages that aren't about one field: a rose Callout (ui/Notice), announced. */
export function FormError({ children, title = "That didn't work" }: { children: ReactNode; title?: string }) {
  if (!children) return null;
  return (
    <Callout tone="critical" role="alert" title={title}>
      {children}
    </Callout>
  );
}
